/**
 * O fundo branco de arte sem transparência (JPEG, PNG exportado chapado) —
 * a mesma decomposição do servidor, em ProductMock/WhiteBackground. Cópia de
 * components/ProductMock/fundoBranco.js da vitrine (brindeme_site).
 *
 * Não basta apagar o que é claro: a borda antisserrilhada é MISTURA da tinta
 * com o branco, e metade vermelho, metade branco passa por tinta cheia — a
 * letra ganhava um contorno rosa-claro. Cada pixel da borda é decomposto
 * contra a tinta vizinha: a fração de tinta vira alfa e a cor vira a da tinta
 * pura. Os limiares são os do servidor; mudar um lado sem o outro faz a
 * prévia prometer o que a composição não entrega.
 */
const RUIDO = 32;
const MIOLO = 0.9;
const RESIDUO = 36;
const RAMPA_BAIXA = 0.08;
const RAMPA_ALTA = 0.92;
const RAIO = 2;

export function arteChapada(pixels) {
    for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] < 253) return false;
    }

    return true;
}

/** Mediana dos cantos quando é clara; senão, branco. */
function fundo(pixels, largura, altura) {
    const cantos = [0, largura - 1, (altura - 1) * largura, altura * largura - 1];
    const canal = c => {
        const valores = cantos.map(i => pixels[i * 4 + c]).sort((a, b) => a - b);

        return Math.round((valores[1] + valores[2]) / 2);
    };
    const cor = [canal(0), canal(1), canal(2)];

    return Math.min(...cor) >= 200 ? cor : [255, 255, 255];
}

/** Maior valor na janela (2R+1)² de cada pixel, em duas passadas. */
function maximoNaJanela(valores, largura, altura) {
    const linhas = new Float64Array(valores.length);

    for (let y = 0; y < altura; y++) {
        const base = y * largura;
        for (let x = 0; x < largura; x++) {
            let maximo = 0;
            for (let nx = Math.max(0, x - RAIO), fim = Math.min(largura - 1, x + RAIO); nx <= fim; nx++) {
                if (valores[base + nx] > maximo) maximo = valores[base + nx];
            }
            linhas[base + x] = maximo;
        }
    }

    const resultado = new Float64Array(valores.length);

    for (let y = 0; y < altura; y++) {
        const de = Math.max(0, y - RAIO);
        const ate = Math.min(altura - 1, y + RAIO);
        for (let x = 0; x < largura; x++) {
            let maximo = 0;
            for (let ny = de; ny <= ate; ny++) {
                if (linhas[ny * largura + x] > maximo) maximo = linhas[ny * largura + x];
            }
            resultado[y * largura + x] = maximo;
        }
    }

    return resultado;
}

/**
 * Tira o fundo no lugar. Distâncias ao quadrado: mesma ordem, sem raiz por
 * pixel.
 */
export function tirarFundoBranco(pixels, largura, altura) {
    const [fr, fg, fb] = fundo(pixels, largura, altura);
    const total = largura * altura;
    const cores = new Uint8ClampedArray(pixels);
    const distancia = new Float64Array(total);

    for (let i = 0; i < total; i++) {
        const dr = fr - cores[i * 4];
        const dg = fg - cores[i * 4 + 1];
        const db = fb - cores[i * 4 + 2];
        distancia[i] = dr * dr + dg * dg + db * db;
    }

    const maisForte = maximoNaJanela(distancia, largura, altura);
    const ruido = RUIDO ** 2;
    const miolo = MIOLO ** 2;
    const residuo = RESIDUO ** 2;

    const pintar = (i, j, alfa) => {
        if (j >= 0) {
            pixels[i * 4] = cores[j * 4];
            pixels[i * 4 + 1] = cores[j * 4 + 1];
            pixels[i * 4 + 2] = cores[j * 4 + 2];
        }
        pixels[i * 4 + 3] = Math.round(alfa * 255);
    };

    for (let i = 0; i < total; i++) {
        const proprio = distancia[i];

        if (proprio > ruido && proprio >= miolo * maisForte[i]) continue;

        if (proprio <= ruido && maisForte[i] <= ruido) {
            pintar(i, -1, 0);
            continue;
        }

        const x = i % largura;
        const y = (i - x) / largura;
        const pr = fr - cores[i * 4];
        const pg = fg - cores[i * 4 + 1];
        const pb = fb - cores[i * 4 + 2];
        let tinta = -1;
        let maior = proprio;
        let fracao = 0;

        for (let ny = Math.max(0, y - RAIO), fimY = Math.min(altura - 1, y + RAIO); ny <= fimY; ny++) {
            for (let nx = Math.max(0, x - RAIO), fimX = Math.min(largura - 1, x + RAIO); nx <= fimX; nx++) {
                const j = ny * largura + nx;

                if (distancia[j] <= maior) continue;

                const ir = fr - cores[j * 4];
                const ig = fg - cores[j * 4 + 1];
                const ib = fb - cores[j * 4 + 2];
                const t = (pr * ir + pg * ig + pb * ib) / distancia[j];
                const er = pr - t * ir;
                const eg = pg - t * ig;
                const eb = pb - t * ib;

                if (er * er + eg * eg + eb * eb > residuo) continue;

                tinta = j;
                maior = distancia[j];
                fracao = t;
            }
        }

        if (proprio <= ruido) {
            pintar(i, tinta, 0);
            continue;
        }

        // Não é mistura com o fundo: é o encontro de duas tintas.
        if (tinta < 0) continue;

        const alfa = Math.max(0, Math.min(1, (fracao - RAMPA_BAIXA) / (RAMPA_ALTA - RAMPA_BAIXA)));
        pintar(i, alfa >= 1 ? -1 : tinta, alfa);
    }
}

/** Alfa mínimo (0..255) para o pixel segurar a caixa: fiapo de alfa não conta. */
const COBERTURA_MINIMA = 8;

/**
 * A caixa do desenho, sem a margem que veio no arquivo — o servidor corta a
 * mesma sobra (ProductMock/ArtMargin) antes de encaixar, e sem isso a prévia
 * mostraria o logo menor que a amostra. Um pixel de folga, como lá. `null`
 * quando não há o que cortar.
 */
export function margemRecortada(pixels, largura, altura) {
    let esquerda = largura;
    let direita = -1;
    let topo = altura;
    let base = -1;

    for (let y = 0; y < altura; y++) {
        for (let x = 0; x < largura; x++) {
            if (pixels[(y * largura + x) * 4 + 3] < COBERTURA_MINIMA) continue;
            if (x < esquerda) esquerda = x;
            if (x > direita) direita = x;
            if (y < topo) topo = y;
            if (y > base) base = y;
        }
    }

    if (direita < 0) return null;

    esquerda = Math.max(0, esquerda - 1);
    topo = Math.max(0, topo - 1);
    direita = Math.min(largura - 1, direita + 1);
    base = Math.min(altura - 1, base + 1);

    if (esquerda === 0 && topo === 0 && direita === largura - 1 && base === altura - 1) return null;

    return { x: esquerda, y: topo, largura: direita - esquerda + 1, altura: base - topo + 1 };
}
